import { supabase } from '../../config/supabase';
import { Admin } from '../../types/admin.types';
import { Role } from '../../types/cms.types';

export interface AdminFilter {
    full_name?: string;
    user_name?: string;
    search?: string;
}

export interface AdminListResponse {
    data: AdminWithRoles[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface AdminWithRoles extends Admin {
    roles: Role[];
}

export interface CreateAdminRequest {
    full_name: string;
    user_name: string;
    pass_word: string;
    role_ids: string[];
    created_by?: string; // Optional for backward compatibility
}

export interface UpdateAdminRequest extends CreateAdminRequest {
    id: string;
}

export class AdminManagementService {
    /**
     * Lấy danh sách admin với phân trang và filter
     */
    static async getAdmins(
        page: number = 1,
        limit: number = 10,
        filters: AdminFilter = {}
    ): Promise<AdminListResponse> {
        try {
            const offset = (page - 1) * limit;
            
            let query = supabase
                .from('admin')
                .select(`
                    *,
                    admin_roles!inner (
                        roles!inner (
                            id,
                            name,
                            description
                        )
                    )
                `, { count: 'exact' });

            // Apply filters
            if (filters.full_name) {
                query = query.ilike('full_name', `%${filters.full_name}%`);
            }

            if (filters.user_name) {
                query = query.ilike('user_name', `%${filters.user_name}%`);
            }

            if (filters.search) {
                query = query.or(`full_name.ilike.%${filters.search}%,user_name.ilike.%${filters.search}%`);
            }

            // Add ordering and pagination
            query = query
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1);

            const { data, error, count } = await query;

            if (error) {
                throw new Error(`Error fetching admins: ${error.message}`);
            }

            const admins: AdminWithRoles[] = (data || []).map((admin: any) => ({
                ...admin,
                pass_word: undefined, // Don't return password
                roles: admin.admin_roles?.map((ar: any) => ar.roles) || []
            }));

            const totalPages = Math.ceil((count || 0) / limit);

            return {
                data: admins,
                total: count || 0,
                page,
                limit,
                totalPages
            };
        } catch (error) {
            console.error('Error in getAdmins:', error);
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết một admin
     */
    static async getAdminById(id: string): Promise<AdminWithRoles | null> {
        try {
            const { data, error } = await supabase
                .from('admin')
                .select(`
                    *,
                    admin_roles (
                        roles (
                            id,
                            name,
                            description
                        )
                    )
                `)
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    return null; // Admin not found
                }
                throw new Error(`Error fetching admin: ${error.message}`);
            }

            return {
                ...data,
                pass_word: undefined, // Don't return password
                roles: data.admin_roles?.map((ar: any) => ar.roles) || []
            };
        } catch (error) {
            console.error('Error in getAdminById:', error);
            throw error;
        }
    }

    /**
     * Tạo admin mới với roles
     */
    static async createAdmin(request: CreateAdminRequest): Promise<AdminWithRoles> {
        try {
            // Begin transaction
            const { data: adminData, error: adminError } = await supabase
                .from('admin')
                .insert([{
                    full_name: request.full_name,
                    user_name: request.user_name,
                    pass_word: request.pass_word,
                    created_by: request.created_by || 'system' // Default to 'system' if not provided
                }])
                .select()
                .single();

            if (adminError) {
                throw new Error(`Error creating admin: ${adminError.message}`);
            }

            // Assign roles to admin
            if (request.role_ids && request.role_ids.length > 0) {
                const adminRolesData = request.role_ids.map(roleId => ({
                    admin_id: adminData.id,
                    role_id: roleId,
                    created_by: request.created_by || 'system' // Add created_by for admin_roles
                }));

                const { error: rolesError } = await supabase
                    .from('admin_roles')
                    .insert(adminRolesData);

                if (rolesError) {
                    // Rollback admin if roles assignment failed
                    await supabase.from('admin').delete().eq('id', adminData.id);
                    throw new Error(`Error assigning roles: ${rolesError.message}`);
                }
            }

            // Get the created admin with roles
            const createdAdmin = await this.getAdminById(adminData.id);
            if (!createdAdmin) {
                throw new Error('Failed to retrieve created admin');
            }

            return createdAdmin;
        } catch (error) {
            console.error('Error in createAdmin:', error);
            throw error;
        }
    }

    /**
     * Cập nhật admin và roles
     */
    static async updateAdmin(request: UpdateAdminRequest): Promise<AdminWithRoles> {
        try {
            // Update admin info
            const updateData: any = {
                full_name: request.full_name,
                user_name: request.user_name,
                updated_at: new Date().toISOString()
            };

            // Only update password if provided
            if (request.pass_word && request.pass_word.trim()) {
                updateData.pass_word = request.pass_word;
            }

            const { data: adminData, error: adminError } = await supabase
                .from('admin')
                .update(updateData)
                .eq('id', request.id)
                .select()
                .single();

            if (adminError) {
                throw new Error(`Error updating admin: ${adminError.message}`);
            }

            // Update roles - delete existing and insert new ones
            const { error: deleteError } = await supabase
                .from('admin_roles')
                .delete()
                .eq('admin_id', request.id);

            if (deleteError) {
                throw new Error(`Error deleting old roles: ${deleteError.message}`);
            }

            // Insert new roles
            if (request.role_ids && request.role_ids.length > 0) {
                const adminRolesData = request.role_ids.map(roleId => ({
                    admin_id: request.id,
                    role_id: roleId,
                    created_by: request.created_by || 'system' // Add created_by for admin_roles
                }));

                const { error: rolesError } = await supabase
                    .from('admin_roles')
                    .insert(adminRolesData);

                if (rolesError) {
                    throw new Error(`Error assigning new roles: ${rolesError.message}`);
                }
            }

            // Get the updated admin with roles
            const updatedAdmin = await this.getAdminById(request.id);
            if (!updatedAdmin) {
                throw new Error('Failed to retrieve updated admin');
            }

            return updatedAdmin;
        } catch (error) {
            console.error('Error in updateAdmin:', error);
            throw error;
        }
    }

    /**
     * Xóa admin
     */
    static async deleteAdmin(id: string): Promise<boolean> {
        try {
            // Delete admin roles first
            const { error: rolesError } = await supabase
                .from('admin_roles')
                .delete()
                .eq('admin_id', id);

            if (rolesError) {
                throw new Error(`Error deleting admin roles: ${rolesError.message}`);
            }

            // Delete admin
            const { error } = await supabase
                .from('admin')
                .delete()
                .eq('id', id);

            if (error) {
                throw new Error(`Error deleting admin: ${error.message}`);
            }

            return true;
        } catch (error) {
            console.error('Error in deleteAdmin:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách roles có sẵn
     */
    static async getAvailableRoles(): Promise<Role[]> {
        try {
            const { data, error } = await supabase
                .from('roles')
                .select('*')
                .order('name', { ascending: true });

            if (error) {
                throw new Error(`Error fetching roles: ${error.message}`);
            }

            return data || [];
        } catch (error) {
            console.error('Error in getAvailableRoles:', error);
            throw error;
        }
    }

    /**
     * Kiểm tra username có tồn tại không (cho validation)
     */
    static async checkUsernameExists(username: string, excludeId?: string): Promise<boolean> {
        try {
            let query = supabase
                .from('admin')
                .select('id')
                .eq('user_name', username);

            if (excludeId) {
                query = query.neq('id', excludeId);
            }

            const { data, error } = await query;

            if (error) {
                throw new Error(`Error checking username: ${error.message}`);
            }

            return (data || []).length > 0;
        } catch (error) {
            console.error('Error in checkUsernameExists:', error);
            throw error;
        }
    }
}
